import { ClosingCTA } from '@/components/content/ClosingCTA';
import { Eyebrow } from '@/components/content/Eyebrow';
import { Reveal } from '@/components/utils/RevealOnScroll';
import { SEO } from '@/components/utils/SEO';

/**
 * /about page.
 * Deck-canonical (Q2 2026 Radium deck slides 3 + 16).
 *
 * Studio four-pillar (slide 3): Wisdom · Technology · Investment · Ecosystem.
 * Two-layers-of-value model (slide 16): Radium-the-platform vs Unicorn-the-solutions-partner.
 */

const studioPillars = [
  {
    title: 'Wisdom',
    body: 'Two decades of building, scaling, and exiting B2B technology companies. Patterns earned, not assumed.',
  },
  {
    title: 'Technology',
    body: 'An AI-driven and SaaS technology portfolio Unicorn invests in, builds with, and licenses to partners — including Radium.',
  },
  {
    title: 'Investment',
    body: 'Pre-seed and seed capital aligned with Unicorn services, deployed into ventures we co-build.',
  },
  {
    title: 'Ecosystem',
    body: 'Entrepreneur-, Investor-, and Founder-in-Residence frameworks. Operators on the ground from inception to succession.',
  },
];

const trustList = [
  'Google',
  'Samsung',
  'Comcast',
  'Salesforce',
  'Hilti',
  'YMCA',
];

const platformBullets = [
  'Open-source orchestration runtime',
  'Multi-tenant SaaS for the personalization application',
  'Continuous improvements funded by the customer base',
  'Documented architecture, auditable substrate',
  'Enterprise SLAs, security, compliance posture',
];

const studioBullets = [
  'Builds the deployment around your stack, not a template',
  'Trains models on your taxonomy and editorial standards',
  'Builds custom adapters and surfaces where needed',
  'Hands off a working deployment — your team operates it, not ours',
  'Platform improvements flow from real customer deployments',
];

const principles = [
  {
    title: 'Build for operators, not demos',
    body: 'The people who use Radium every day are content ops, data science, and compliance. We design for them, not for the keynote.',
  },
  {
    title: 'Respect the boundary between data and product',
    body: "Your CDP is good at what it does. Your CMS is good at what it does. Radium operates between them, and we don't reach into work that's already solved.",
  },
  {
    title: 'Explainability is non-negotiable',
    body: 'Every ranking decision returns its reasoning. Every model has a version. Every change has an audit trail. "The model said so" isn\'t a deliverable.',
  },
  {
    title: 'Earned personalization, not imposed',
    body: 'The user has to benefit before we benefit. Personalization that feels imposed degrades trust faster than no personalization at all.',
  },
  {
    title: 'Open by default',
    body: 'The platform is open-source. The substrate is auditable. The architecture is documented in public. We build in the open because infrastructure people trust should be infrastructure they can read.',
  },
];

export function About() {
  return (
    <>
      <SEO
        title="About — Radium · Built by Unicorn"
        description="Unicorn is an applied venture studio: Wisdom, Technology, Investment, and Ecosystem in one model. Radium is the platform; Unicorn is the solutions partner."
        canonical="https://radium.love/about"
        ogImage="/og/og-about.png"
      />

      <section className="page-hero" style={{ paddingBottom: 56 }}>
        <div className="container">
          <Reveal>
            <Eyebrow>The studio</Eyebrow>
          </Reveal>
          <Reveal delay={80}>
            <h1
              className="text-display"
              style={{ marginTop: 24, maxWidth: 1000 }}
            >
              Unicorn is an{' '}
              <span style={{ color: 'var(--accent)' }}>
                applied venture studio
              </span>
              .
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p
              className="text-body-lg"
              style={{
                marginTop: 24,
                maxWidth: 880,
                color: 'var(--ink-secondary)',
              }}
            >
              Beyond traditional incubators, venture funds, and consulting models —
              Unicorn accelerates the path from concept to scale through a growing
              portfolio of AI-driven and SaaS technology, an ecosystem of
              operator-investors, and the in-residence frameworks that put
              expertise where it's needed.
            </p>
          </Reveal>
        </div>
      </section>

      <section
        className="section"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <div className="container">
          <div
            className="studio-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 16,
            }}
          >
            {studioPillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 70}>
                <div
                  className="surface"
                  style={{
                    padding: 28,
                    height: '100%',
                    borderRadius: 8,
                    borderTop: '2px solid var(--accent)',
                  }}
                >
                  <h3 className="text-h4" style={{ marginBottom: 12 }}>
                    {p.title}
                  </h3>
                  <p
                    style={{
                      color: 'var(--ink-secondary)',
                      fontSize: 14,
                      lineHeight: 1.65,
                    }}
                  >
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={400}>
            <p
              className="text-mono"
              style={{
                marginTop: 32,
                textAlign: 'center',
                fontSize: 12,
                color: 'var(--ink-muted)',
                letterSpacing: '0.04em',
              }}
            >
              Trusted by{' '}
              {trustList.map((name, i) => (
                <span key={name}>
                  <span style={{ color: 'var(--ink-secondary)' }}>{name}</span>
                  {i < trustList.length - 1 ? ' · ' : ''}
                </span>
              ))}
              , and many more.
            </p>
          </Reveal>
        </div>
        <style>{`
          @media (max-width: 980px) { .studio-grid { grid-template-columns: 1fr 1fr !important; } }
          @media (max-width: 560px) { .studio-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>

      <section
        className="section"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <div className="container">
          <Reveal>
            <Eyebrow>The studio model</Eyebrow>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="text-h2" style={{ marginTop: 16, maxWidth: 820 }}>
              Two layers of value. <br />
              <span style={{ color: 'var(--accent)' }}>One engagement model.</span>
            </h2>
          </Reveal>
          <div
            className="layers-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 24,
              marginTop: 48,
            }}
          >
            <Reveal delay={140}>
              <div
                className="surface"
                style={{
                  padding: 36,
                  borderRadius: 8,
                  height: '100%',
                  borderTop: '2px solid var(--accent)',
                }}
              >
                <div
                  className="text-mono"
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.06em',
                    color: 'var(--ink-muted)',
                    marginBottom: 16,
                  }}
                >
                  RADIUM — THE PLATFORM
                </div>
                <h3 className="text-h3" style={{ marginBottom: 24 }}>
                  Productized backbone. Predictable scale.
                </h3>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    color: 'var(--ink-secondary)',
                    lineHeight: 1.7,
                  }}
                >
                  {platformBullets.map((b) => (
                    <li key={b} style={{ marginBottom: 8 }}>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={220}>
              <div
                className="surface"
                style={{
                  padding: 36,
                  borderRadius: 8,
                  height: '100%',
                  border: '1px solid rgba(0, 218, 165, 0.4)',
                  boxShadow:
                    '0 0 40px var(--radium-glow), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                }}
              >
                <div
                  className="text-mono"
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.06em',
                    color: 'var(--accent)',
                    marginBottom: 16,
                  }}
                >
                  UNICORN — THE SOLUTIONS PARTNER
                </div>
                <h3 className="text-h3" style={{ marginBottom: 24 }}>
                  Builds it custom. Hands it off working.
                </h3>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    color: 'var(--ink-secondary)',
                    lineHeight: 1.7,
                  }}
                >
                  {studioBullets.map((b) => (
                    <li key={b} style={{ marginBottom: 8 }}>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
        <style>{`@media (max-width: 880px) { .layers-grid { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      <section
        className="section"
        style={{ borderTop: '1px solid var(--border-subtle)' }}
      >
        <div className="container">
          <Reveal>
            <Eyebrow>Principles</Eyebrow>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="text-h2" style={{ marginTop: 16, maxWidth: 720 }}>
              What we believe.
            </h2>
          </Reveal>
          <div
            style={{
              marginTop: 48,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
            }}
            className="principles-grid"
          >
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 60}>
                <div
                  className="glass glass--settled"
                  style={{ padding: 28, height: '100%', borderRadius: 12 }}
                >
                  <div
                    className="text-mono"
                    style={{
                      fontSize: 12,
                      color: 'var(--accent)',
                      letterSpacing: '0.04em',
                      marginBottom: 12,
                    }}
                  >
                    0{i + 1}
                  </div>
                  <h3 className="text-h4" style={{ marginBottom: 12 }}>
                    {p.title}
                  </h3>
                  <p
                    style={{
                      color: 'var(--ink-secondary)',
                      fontSize: 14,
                      lineHeight: 1.6,
                    }}
                  >
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <style>{`@media (max-width: 760px) { .principles-grid { grid-template-columns: 1fr !important; } }`}</style>
      </section>

      <ClosingCTA
        h2="Want to see how the studio model works in practice?"
        body="Visit Unicorn at unicorn.love, or get in touch — we'll show you what a custom Radium deployment looks like for your stack."
        secondary={{
          label: 'Visit unicorn.love',
          href: 'https://unicorn.love',
        }}
      />
    </>
  );
}
